import actionTypes from 'redux/actions/actionTypes'

import makeUniqueId from 'helpers/makeUniqueId'
import errorCodes from 'helpers/enums/errorCodes'
import { htmlToPlainText } from 'helpers/html/sanitizer'

const defaultTimer = {
	value: null,
}

const defaultState = {
	entityUID: '',
	presentationId: '',
	title: {},
	questions: [],
	toolbar: {},
	isModified: false,
	timer: {
		...defaultTimer,
	},
	cancelUrl: '',
}

// TODO: Change serverId to id and id to clientId
const defaultQuestion = {
	id: '',
	serverId: '',
	icon: {},
	text: '',
	textTouched: true,
	answers: [],
	collapsed: false,
	touched: true,
	status: '',
}

const defaultAnswer = {
	id: '',
	text: '',
	textTouched: true,
	correct: false,
}

const defaultTitle = {
	text: '',
}

const defaultIcon = {
	id: 0,
	uploadId: 0,
	type: '',
	versions: {},
	link: '',
	status: '',
	message: '',
}

function createInitialize(state, action) {
	return {
		...state,
		entityUID: action.quizUID,
	}
}

function createInitializeSuccess(state, action) {
	const newState = {...state}
	newState.title = {
		...defaultTitle,
		text: action.data.title,
	}
	newState.presentationId = action.data.presentationId
	newState.questions = action.data.questions.map(question => ({
		...defaultQuestion,
		id: makeUniqueId(),
		serverId: question.id,
		icon: {
			id: (question.icon.id) ? question.icon.id : 0,
			uploadId: 0,
			type: (question.icon.type) ? question.icon.type : '',
			versions: (question.icon.versions) ? question.icon.versions : {},
			link: (question.icon.link) ? question.icon.link : '',
			status: (question.icon.status) ? question.icon.status : '',
			message: '',
		},
		text: question.text,
		plainText: htmlToPlainText(question.text),
		answers: question.answers.map(answer => ({
			...defaultAnswer,
			id: makeUniqueId(),
			text: answer.text,
			correct: answer.correct,
		})),
	}))
	newState.timer = {
		value: action.data.timer,
	}
	newState.cancelUrl = action.data.cancelUrl

	return newState
}

function createDuplicateQuestion(state, action) {
	let arrayPosition = 0
	const newState = {...state}
	const newQuestion = {
		...state.questions.find((question, index) => {
			if (question.id === action.duplicateQuestionId) {
				arrayPosition = index + 1
				return true
			}
		}),
	}
	newQuestion.id = action.questionId
	newQuestion.serverId = ''
	newQuestion.status = 'saving'

	newQuestion.answers = newQuestion.answers.map(answer => ({
		...answer,
		id: makeUniqueId(),
	}))

	newState.questions = [...state.questions.slice(0, arrayPosition), newQuestion, ...state.questions.slice(arrayPosition)]
	return newState
}

function createDeleteQuestion(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: 'deleting',
				}
			} else {
				return question
			}
		}),
	}
}

function createDeleteQuestionSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.filter(question => question.id !== action.questionId),
	}
}

function createAddAnswer(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					answers: [ ...question.answers, {...defaultAnswer, id: makeUniqueId()} ],
					status: 'saving',
				}
			} else {
				return question
			}
		}),
	}
}

function createAddQuestion(state, action) {
	return {
		...state,
		questions: [
			...state.questions,
			{
				...defaultQuestion,
				id: action.questionId,
				icon: {
					...defaultIcon,
				},
				answers: [
					{
						...defaultAnswer,
						id: makeUniqueId(),
					},
					{
						...defaultAnswer,
						id: makeUniqueId(),
					},
				],
				status: 'saving',
			},
		],
	}
}

function createMoveQuestion(state, action) {
	const newState = {...state}
	newState.isModified = true
	newState.questions = [...state.questions]

	const draggedIndex = state.questions.findIndex(question => question.id === action.draggedQuestion)
	const replacedIndex = state.questions.findIndex(question => question.id === action.replacedQuestion)

	newState.questions[draggedIndex] = state.questions[replacedIndex]
	newState.questions[replacedIndex] = state.questions[draggedIndex]
	return newState
}

function createMoveAnswer(state, action) {

	const newState = {...state}
	newState.questions = [...state.questions]

	// search dragged and replaced question position
	const draggedQuestionIndex = state.questions.findIndex(question => question.id === action.draggedAnswer.questionId)

	// search dragged and replaced answers positions
	const draggedAnswerIndex = state.questions[draggedQuestionIndex].answers.findIndex(
		answer => answer.id === action.draggedAnswer.answerId
	)
	const replacedAnswerIndex = state.questions[draggedQuestionIndex].answers.findIndex(
		answer => answer.id === action.replacedAnswer.answerId
	)

	newState.questions[draggedQuestionIndex] = {...state.questions[draggedQuestionIndex]}
	newState.questions[draggedQuestionIndex].answers = [...state.questions[draggedQuestionIndex].answers]

	// move replaced to dragged position
	newState.questions[draggedQuestionIndex].answers[draggedAnswerIndex] = {
		...state.questions[draggedQuestionIndex].answers[replacedAnswerIndex],
	}

	// move dragged to replaced position
	newState.questions[draggedQuestionIndex].answers[replacedAnswerIndex] = {
		...state.questions[draggedQuestionIndex].answers[draggedAnswerIndex],
	}

	return newState
}

function createToggleCollapseQuestion(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (action.questionId === question.id) {
				return {
					...question,
					collapsed: !question.collapsed,
				}
			} else {
				return question
			}
		}),
	}
}

function createCollapseAll(state) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.collapsed === false) {
				return {
					...question,
					collapsed: true,
				}
			} else {
				return question
			}
		}),
	}
}

function createExpandAll(state) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.collapsed === true) {
				return {
					...question,
					collapsed: false,
				}
			} else {
				return question
			}
		}),
	}
}

function createQuestionChange(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					text: action.value,
					status: 'changed',
					// Reset hasExceedChars prop
					hasExceedChars: false,
				}
			} else {
				return question
			}
		}),
	}
}

function createQuestionSaveSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					serverId: action.serverId,
					status: '',
				}
			} else {
				return question
			}
		}),
	}
}

function createQuestionUpdate(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: 'saving',
				}
			} else {
				return question
			}
		}),
	}
}

function createQuestionSaveError(state, action) {
	return {
		...state,
		questions: state.questions.filter(question => question.id !== action.questionId),
	}
}

function createQuestionUpdateSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: '',
				}
			} else {
				return question
			}
		}),
	}
}

function updateQuestionError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					textTouched: false,
					touched: false,
					status: '',
					hasExceedChars: action.error.error_code === errorCodes.EXCEED_MAX_CHARS,
				}
			} else {
				return question
			}
		}),
	}
}

function updateQuestionPlainText(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					plainText: htmlToPlainText(question.text),
				}
			} else {
				return question
			}
		}),
	}
}

function createAnswerChange(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: 'changed',
					answers: question.answers.map(answer => {
						if (answer.id === action.answerId) {
							return {
								...answer,
								text: action.value,
							}
						} else {
							return answer
						}
					}),
				}
			} else {
				return question
			}
		}),
	}
}

function createTitleChange(state, action) {
	return {
		...state,
		title: {
			...state.title,
			text: action.value,
		},
	}
}

function createTitleSave(state) {
	return {
		...state,
		title: {
			...state.title,
			status: 'saving',
		},
	}
}

function createTitleSaveSuccess(state) {
	return {
		...state,
		isModified: true,
		title: {
			...state.title,
			status: '',
		},
	}
}

function createValidationError(state) {
	return {
		...state,
		questions: state.questions.map(question => ({
			...question,
			textTouched: false,
			touched: false,
			answers: question.answers.map(answer => ({
				...answer,
				textTouched: false,
			})),
		})),
	}
}

function createDeleteAnswer(state, action) {
	const newState = {...state}
	newState.isModified = true
	const questionIndex = state.questions.findIndex(question => question.id === action.questionId)

	newState.questions = [...state.questions]
	newState.questions[questionIndex] = {
		...state.questions[questionIndex],
		answers: state.questions[questionIndex].answers.filter(answer => answer.id !== action.answerId),
		status: 'saving',
	}

	return newState
}

function createAnswerCheckboxChange(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					touched: true,
					answers: question.answers.map(answer => {
						if (answer.id === action.answerId) {
							return {
								...answer,
								correct: (answer.correct) ? 0 : 1,
							}
						} else {
							return answer
						}
					}),
					status: 'saving',
				}
			} else {
				return question
			}
		}),
	}
}

function createQuestionFocus(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					textTouched: true,
				}
			} else {
				return question
			}
		}),
	}
}

function createAnswerFocus(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					answers: question.answers.map(answer => {
						if (answer.id === action.answerId) {
							return {
								...answer,
								textTouched: true,
							}
						} else {
							return answer
						}
					}),
				}
			} else {
				return question
			}
		}),
	}
}

function uploaderUploadSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						id: action.icon.id,
						type: action.icon.type,
						versions: action.icon.versions,
						link: action.icon.link,
						status: action.icon.status,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function uploaderUploadError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						message: action.message,
						status: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function processingIconSuccess(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.icon.id === action.icon.id) {
				return {
					...question,
					icon: {
						...question.icon,
						...action.icon,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function processingIconError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.icon.id === action.action.icon.id) {
				return {
					...question,
					icon: {
						...question.icon,
						message: action.message,
						status: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function deleteReferenceMedia(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...defaultIcon,
					},
				}
			} else {
				return question
			}
		}),
	}
}

function showReferenceMediaLoading(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						status: 'uploading',
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function addReferenceMediaClick(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function setQuizAsModified(state) {
	if (state.isModified) {
		return state
	}
	return {...state,
		isModified: true,
	}
}

function saveTimer(state, action) {
	return {
		...state,
		isModified: true,
		timer: {
			value: action.value,
		},
	}
}

function deleteTimer(state) {
	return {
		...state,
		timer: {
			value: null,
		},
		isModified: true,
	}
}

const quizReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.QUIZ_INITIALIZE:
		case actionTypes.QUIZ_INITIALIZE_STANDALONE: {
			return createInitialize(state, action)
		}
		case actionTypes.QUIZ_INITIALIZE_SUCCESS: {
			return createInitializeSuccess(state, action)
		}
		case actionTypes.QUIZ_DUPLICATE_QUESTION_VALIDATION_SUCCESS: {
			return createDuplicateQuestion(state, action)
		}
		case actionTypes.QUIZ_DELETE_QUESTION_VALIDATION_SUCCESS: {
			return createDeleteQuestion(state, action)
		}
		case actionTypes.QUIZ_DELETE_QUESTION_SUCCESS: {
			return createDeleteQuestionSuccess(state, action)
		}
		case actionTypes.QUIZ_ADD_ANSWER_VALIDATION_SUCCESS: {
			return createAddAnswer(state, action)
		}
		case actionTypes.QUIZ_ADD_QUESTION_VALIDATION_SUCCESS: {
			return createAddQuestion(state, action)
		}
		case actionTypes.QUIZ_DRAG_QUESTION: {
			return createMoveQuestion(state, action)
		}
		case actionTypes.QUIZ_DRAG_ANSWER: {
			return createMoveAnswer(state, action)
		}
		case actionTypes.QUIZ_TOGGLE_COLLAPSE_QUESTION: {
			return createToggleCollapseQuestion(state, action)
		}
		case actionTypes.QUIZ_COLLAPSE_ALL: {
			return createCollapseAll(state, action)
		}
		case actionTypes.QUIZ_EXPAND_ALL: {
			return createExpandAll(state, action)
		}
		case actionTypes.QUIZ_QUESTION_CHANGE: {
			return createQuestionChange(state, action)
		}
		case actionTypes.QUIZ_QUESTION_SAVE_SUCCESS: {
			return createQuestionSaveSuccess(state, action)
		}
		case actionTypes.QUIZ_QUESTION_TO_PLAIN_TEXT: {
			return updateQuestionPlainText(state, action)
		}
		case actionTypes.QUIZ_ANSWER_SAVE:
		case actionTypes.QUIZ_QUESTION_SAVE:
		case actionTypes.QUIZ_QUESTION_UPDATE: {
			return createQuestionUpdate(state, action)
		}
		case actionTypes.QUIZ_QUESTION_UPDATE_SUCCESS: {
			return createQuestionUpdateSuccess(state, action)
		}
		case actionTypes.QUIZ_QUESTION_UPDATE_ERROR: {
			return updateQuestionError(state, action)
		}
		case actionTypes.QUIZ_QUESTION_SAVE_ERROR: {
			return createQuestionSaveError(state, action)
		}
		case actionTypes.QUIZ_ANSWER_CHANGE: {
			return createAnswerChange(state, action)
		}
		case actionTypes.QUIZ_TITLE_CHANGE: {
			return createTitleChange(state, action)
		}
		case actionTypes.QUIZ_TITLE_SAVE: {
			return createTitleSave(state, action)
		}
		case actionTypes.QUIZ_TITLE_SAVE_SUCCESS: {
			return createTitleSaveSuccess(state, action)
		}
		case actionTypes.QUIZ_VALIDATION_ERROR: {
			return createValidationError(state, action)
		}
		case actionTypes.QUIZ_DELETE_ANSWER_VALIDATION_SUCCESS: {
			return createDeleteAnswer(state, action)
		}
		case actionTypes.QUIZ_ANSWER_CHECKBOX_CHANGE: {
			return createAnswerCheckboxChange(state, action)
		}
		case actionTypes.QUIZ_QUESTION_FOCUS: {
			return createQuestionFocus(state, action)
		}
		case actionTypes.QUIZ_ANSWER_FOCUS: {
			return createAnswerFocus(state, action)
		}
		case actionTypes.QUIZ_UPLOADER_UPLOAD_SUCCESS: {
			return uploaderUploadSuccess(state, action)
		}
		case actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA_ERROR:
		case actionTypes.QUIZ_UPLOADER_VALIDATE_ERROR:
		case actionTypes.QUIZ_UPLOADER_UPLOAD_ERROR:
		case actionTypes.QUIZ_UPLOADER_VALIDATE_CONTENT_ERROR: {
			return uploaderUploadError(state, action)
		}
		case actionTypes.PROCESSING_ICON_SUCCESS: {
			return processingIconSuccess(state, action)
		}
		case actionTypes.PROCESSING_ICON_ERROR: {
			return processingIconError(state, action)
		}
		case actionTypes.QUIZ_DELETE_REFERENCE_MEDIA_SUCCESS: {
			return deleteReferenceMedia(state, action)
		}
		case actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA: {
			return showReferenceMediaLoading(state, action)
		}
		case actionTypes.QUIZ_ADD_REFERENCE_MEDIA_CLICK: {
			return addReferenceMediaClick(state, action)
		}
		case actionTypes.QUIZ_DROP_ANSWER:
		case actionTypes.QUIZ_DROP_QUESTION:
		{
			return setQuizAsModified(state, action)
		}
		case actionTypes.QUIZ_SAVE_TIMER:
			return saveTimer(state, action)
		case actionTypes.QUIZ_DELETE_TIMER:
			return deleteTimer(state, action)
		default: return state
	}
}

export default quizReducer
